import React, { useState, useEffect } from 'react';
import { Loader2, Save } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { API } from '../../utils/kitchenTasks';
import { formatDateFR } from '../../utils/dateFormat';
import { formatApiError } from '../../services/api';

const fieldCls = 'h-11 rounded-xl bg-gray-100 border-gray-200 text-gray-900 text-sm';

// Le chef ajuste la commande du jour (quantités, lignes écartées avec motif, note) avant validation
export default function OrderLinesDialog({ order, onOpenChange, onSaved }) {
  const [lines, setLines] = useState([]);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!order) return;
    setLines([...(order.items || []).map(i => ({ ...i, accepted: true, reason: '' })), ...(order.rejected_lines || []).map(l => ({ ...l, accepted: false }))]);
    setNote(order.note || '');
  }, [order]);

  const patch = (idx, p) => setLines(lines.map((l, i) => (i === idx ? { ...l, ...p } : l)));
  const save = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await axios.patch(`${API}/order-requests/orders/${order.id}`, { request_ids: order.source_request_ids || [], date: order.date, note, lines: lines.map(l => ({ ...l, quantity: parseFloat(l.quantity) || 0, source_request_ids: l.source_request_ids || [] })) });
      toast.success('Commande mise à jour');
      onSaved?.(); onOpenChange(false);
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!order} onOpenChange={(v) => { if (!busy) onOpenChange(v); }}>
      <DialogContent className="bg-white max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="order-lines-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl text-gray-900">Commande du {order ? formatDateFR(order.date) : ''}</DialogTitle>
          <DialogDescription>Les demandes des collaborateurs sont additionnées par produit. Ajustez les quantités finales ou écartez une ligne avec un motif.</DialogDescription>
        </DialogHeader>
        <div><Label className="text-gray-700">Note pour la commande</Label><Input value={note} onChange={(e) => setNote(e.target.value)} className={`mt-1 ${fieldCls}`} data-testid="order-lines-note" /></div>
        <div className="space-y-2 mt-2">
          {lines.map((l, idx) => (
            <div key={`${l.product_id}-${l.unit}`} className={`border rounded-xl px-3 py-2 ${l.accepted ? 'border-gray-200' : 'border-red-200 bg-red-50/40'}`} data-testid={`order-line-${idx}`}>
              <div className="flex items-center gap-2">
                <input type="checkbox" checked={l.accepted} onChange={(e) => patch(idx, { accepted: e.target.checked })} className="w-6 h-6 accent-neon-pink" data-testid={`order-line-accept-${idx}`} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900">{l.product_name}</p>
                  <p className="text-xs text-gray-400">{(l.requesters || []).join(' + ') || l.category}{l.requested_quantity != null && Number(l.requested_quantity) !== Number(l.quantity) ? ` · demandé ${l.requested_quantity} ${l.unit}` : ''}</p>
                </div>
                <Input type="number" step="any" min="0" value={l.quantity} onChange={(e) => patch(idx, { quantity: e.target.value })} className="w-24 h-10 rounded-lg bg-gray-100 border-gray-200 text-right" data-testid={`order-line-qty-${idx}`} />
                <span className="text-xs text-gray-500 w-8">{l.unit}</span>
              </div>
              {!l.accepted && <Input value={l.reason || ''} onChange={(e) => patch(idx, { reason: e.target.value })} placeholder="Motif d'écartement" className="mt-2 h-10 rounded-lg bg-white border-red-200 text-sm" data-testid={`order-line-reason-${idx}`} />}
            </div>
          ))}
        </div>
        <Button onClick={save} disabled={busy} className="w-full h-12 rounded-full bg-gray-900 text-white font-semibold mt-3" data-testid="order-lines-save-btn">{busy ? <Loader2 size={16} className="animate-spin mr-2" /> : <Save size={16} className="mr-2" />}Enregistrer les quantités</Button>
      </DialogContent>
    </Dialog>
  );
}
